import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { resendSignupOtp, verifySignupOtp } from '../services/authService'
import { useAuth } from '../hooks/useAuth'

export const SIGNUP_VERIFY_EMAIL_KEY = 'signupVerifyEmail'

const VerifyEmailCode = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { refreshProfile } = useAuth()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState('')
  const [resendMessage, setResendMessage] = useState('')

  useEffect(() => {
    const fromState = location.state?.email
    const fromStorage = sessionStorage.getItem(SIGNUP_VERIFY_EMAIL_KEY)
    const resolved = (fromState || fromStorage || '').trim().toLowerCase()
    if (resolved) {
      setEmail(resolved)
      sessionStorage.setItem(SIGNUP_VERIFY_EMAIL_KEY, resolved)
      if (location.state?.notice) {
        setResendMessage(location.state.notice)
      }
    } else {
      navigate('/', { replace: true })
    }
  }, [location.state, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setResendMessage('')

    const trimmed = code.replace(/\s/g, '')
    if (!trimmed) {
      setError('Please enter the code from your email.')
      return
    }
    if (trimmed.length < 6) {
      setError('The code should be 6 digits.')
      return
    }

    setLoading(true)
    try {
      let profileOverride = location.state?.profile || {}
      try {
        const stored = sessionStorage.getItem(`${SIGNUP_VERIFY_EMAIL_KEY}_profile`)
        if (stored && !profileOverride.firstName) {
          profileOverride = JSON.parse(stored)
        }
      } catch {
        // ignore bad stored profile
      }
      const result = await verifySignupOtp(email, trimmed, profileOverride)
      if (!result.success) {
        setError(result.error)
        return
      }
      sessionStorage.removeItem(SIGNUP_VERIFY_EMAIL_KEY)
      sessionStorage.removeItem(`${SIGNUP_VERIFY_EMAIL_KEY}_profile`)
      try {
        await refreshProfile()
      } catch {
        // Home will load profile from the new session
      }
      navigate('/', { replace: true })
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setError('')
    setResendMessage('')
    setResending(true)
    try {
      const result = await resendSignupOtp(email)
      if (result.success) {
        setResendMessage('A new 6-digit code was sent to your email.')
      } else {
        setError(result.error || 'Failed to resend code.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setResending(false)
    }
  }

  if (!email) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <i className="fas fa-spinner fa-spin text-4xl text-primary"></i>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 sm:px-6 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center text-primary hover:text-purple-700 transition mb-4"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Home
            </Link>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-envelope-open-text text-primary text-2xl"></i>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Verify your email
              </h1>
              <p className="text-sm text-gray-600">
                We sent a 6-digit code to{' '}
                <span className="font-semibold text-gray-900">{email}</span>
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle text-red-600 mr-2"></i>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {resendMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <i className="fas fa-check-circle text-green-600 mr-2"></i>
                <p className="text-green-700 text-sm">{resendMessage}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification code
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder="000000"
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl tracking-[0.4em] focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-secondary text-white py-3 rounded-lg hover:bg-gray-800 transition text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Verifying...</span>
                </>
              ) : (
                'Verify code'
              )}
            </button>
          </form>

          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="w-full mt-3 text-primary hover:underline text-sm font-medium disabled:opacity-50"
          >
            {resending ? 'Sending...' : 'Resend code'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmailCode
